import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicBookingApi } from '../../api/publicBooking';
import { ApiError } from '../../api/client';
import type { PublicBookingInfo } from '../../types/publicBooking';
import WalkInCheckIn from './WalkInCheckIn';
import BookWithPayment from './BookWithPayment';

/**
 * Entry point for the shareable booking links providers generate
 * (`/book?token=…`). Both link kinds land here; the token's own
 * `paymentRequired` flag decides which of the two flows renders, so a customer
 * never has to know which link they were sent.
 *
 * Public route — no login, and no portal chrome.
 */
const PublicBooking = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');

	const [info, setInfo] = useState<PublicBookingInfo | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!token) {
			setIsLoading(false);
			return;
		}

		let cancelled = false;
		const load = async () => {
			setIsLoading(true);
			setError(null);
			try {
				const data = await publicBookingApi.getInfo(token);
				if (!cancelled) setInfo(data);
			} catch (err) {
				if (cancelled) return;
				if (err instanceof ApiError && (err.isNotFound() || err.status === 410)) {
					setError(
						'This booking link is no longer active. Please ask the salon for a new link.'
					);
				} else {
					setError(
						err instanceof ApiError && err.message
							? err.message
							: 'We could not load this booking page. Please try again in a moment.'
					);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [token]);

	if (!token) {
		return (
			<BookingMessage
				icon="🔗"
				title="Missing booking link"
				text="This page needs the full link the salon shared with you. Please open the link again from your message or email."
			/>
		);
	}

	if (isLoading) {
		return (
			<div className="pb pb--walkin">
				<div className="pb__state">
					<div className="pb__spinner" />
					<div className="pb__state-text">Loading booking page…</div>
				</div>
			</div>
		);
	}

	if (error || !info) {
		return (
			<BookingMessage
				icon="⚠️"
				title="Booking unavailable"
				text={error ?? 'We could not load this booking page.'}
			/>
		);
	}

	return info.paymentRequired ? (
		<BookWithPayment token={token} info={info} />
	) : (
		<WalkInCheckIn token={token} info={info} />
	);
};

const BookingMessage = ({ icon, title, text }: { icon: string; title: string; text: string }) => (
	<div className="pb pb--walkin">
		<div className="pb__state">
			<div className="pb__state-icon">{icon}</div>
			<div className="pb__state-title">{title}</div>
			<div className="pb__state-text">{text}</div>
		</div>
	</div>
);

export default PublicBooking;
